import { Button, Col, DatePicker, Input, Row, Segmented, Select, Space } from 'antd'
import { ReactNode } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faCalendarXmark, faCalendarCheck, faWarning, faPaperPlane } from '@fortawesome/pro-solid-svg-icons'
import { faArrowDownLong, faArrowUpLong } from '@fortawesome/pro-regular-svg-icons'
import { faPaperPlane as faPaperPlaneDuoTone, faThumbsUp, faXmarkToSlot } from '@fortawesome/pro-duotone-svg-icons'
import { blue, green, grey, red } from '@ant-design/colors'
import {
  EstimateStatuses, estimateStatusName, ProjectStatuses, projectStatusName
} from '../../common/constants'
import MultiSelect from '../common/FormControls/MultiSelect'
import { boardSortColumn, boardSortOptions, filterColumnWidths } from './jobListFiltersModel'
import ControlField from '../common/ControlField'
import { buildProjectTypeOptions, JobListFilterState } from './useJobListFilters'
import useServiceTicketsEnabled from '../../hooks/useServiceTicketsEnabled'
import './JobListFilters.css'

export const statusIcons = {
  [ProjectStatuses.Unscheduled]: <FontAwesomeIcon icon={faCalendarXmark} color={grey.primary} size="lg" />,
  [ProjectStatuses.Scheduled]: <FontAwesomeIcon icon={faCalendarCheck} color={blue.primary} size="lg" />,
  [ProjectStatuses.WaitingOnParts]: <FontAwesomeIcon icon={faWarning} color={red.primary} size="lg" />,
  [ProjectStatuses.Completed]: <FontAwesomeIcon icon={faCheck} color={green.primary} size="lg" />,
}

export const estimateStatusIcons = {
  [EstimateStatuses.Unsent]: <FontAwesomeIcon icon={faPaperPlane} color={grey[0]} size="lg" />,
  [EstimateStatuses.Sent]: <FontAwesomeIcon icon={faPaperPlaneDuoTone} color={blue.primary} size="lg" />,
  [EstimateStatuses.Approved]: <FontAwesomeIcon icon={faThumbsUp} color={green.primary} size="lg" />,
  [EstimateStatuses.Lost]: <FontAwesomeIcon icon={faXmarkToSlot} color={red.primary} size="lg" />
}

type Props = {
  filters: JobListFilterState
  viewToggle?: ReactNode
  densityToggle?: ReactNode
  showSort?: boolean
}

const inputStyle: React.CSSProperties = { width: '100%', fontWeight: 'initial' }

const JobListFilters = ({ filters, viewToggle, densityToggle, showSort }: Props) => {
  const projectTypeOptions = buildProjectTypeOptions(useServiceTicketsEnabled())
  const {
    searchText, setSearchText,
    selectedStatuses, setSelectedStatuses,
    selectedEstimateStatuses, setSelectedEstimateStatuses,
    estimateJobType, setEstimateJobType,
    startDate, setStartDate, endDate, setEndDate,
    archived, setArchived,
    sortColumn, setSortColumn, sortOrder, setSortOrder,
    deleted, sold
  } = filters

  const statusOptions = Object.values(ProjectStatuses).map(s => ({
    label: <Space>{statusIcons[s]}{projectStatusName(s)}</Space>,
    value: s
  }))

  const estimateStatusOptions = Object.values(EstimateStatuses).map(s => ({
    label: <Space>{estimateStatusIcons[s]}{estimateStatusName(s)}</Space>,
    value: s
  }))

  const widths = filterColumnWidths({
    sold, deleted, hasDensityToggle: !!densityToggle, hasSort: showSort
  })
  const sortOptions = boardSortOptions({ sold, deleted: !!deleted })
  const descending = sortOrder !== 'ascend'

  return <div className="job-list-filters kanban-filter-row">
    <Row gutter={[16, 16]} align="bottom">
      <Col xl={widths.search} span={24}>
        <ControlField label="Search">
          <Input.Search
            placeholder="Search job name, number, customer, or property"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={inputStyle}
          />
        </ControlField>
      </Col>
      <Col xl={widths.soldDateOrEstimateStatus} span={12}>
        {sold &&
          <ControlField label="Sold Date">
            <DatePicker.RangePicker
              value={[startDate, endDate]}
              style={inputStyle}
              onChange={(range) => {
                if (range) {
                  setStartDate(range[0])
                  setEndDate(range[1])
                } else {
                  setStartDate(null)
                  setEndDate(null)
                }
              }} />
          </ControlField>
        }
        {!deleted && !sold &&
          <ControlField label="Estimate Status">
            <MultiSelect
              options={estimateStatusOptions}
              selectedValues={selectedEstimateStatuses}
              style={inputStyle}
              allSelectedText="All Statuses Selected"
              onChange={setSelectedEstimateStatuses}
              placeholder="No statuses selected"
              maxTagCount={4}
            />
          </ControlField>
        }
      </Col>
      <Col xl={widths.jobStatusOrEstimateType} span={12}>
        {!deleted && sold &&
          <ControlField label="Job Status">
            <MultiSelect
              options={statusOptions}
              selectedValues={selectedStatuses}
              style={inputStyle}
              allSelectedText="All Statuses Selected"
              onChange={setSelectedStatuses}
              placeholder="No statuses selected"
              maxTagCount={4}
            />
          </ControlField>
        }
        {!sold &&
          <ControlField label="Estimate Type">
            <Segmented
              options={projectTypeOptions}
              value={estimateJobType}
              onChange={setEstimateJobType}
            />
          </ControlField>
        }
      </Col>
      {!deleted && sold && <Col xl={widths.visibility} span={12}>
        <ControlField label="Visibility">
          <Segmented
            options={[
              { label: 'Active', value: false },
              { label: 'Archived', value: true }
            ]}
            value={archived}
            onChange={setArchived}
          />
        </ControlField>
      </Col>}
      {showSort && <Col xl={widths.sort} span={12}>
        <ControlField label="Sort">
          <Space.Compact style={inputStyle}>
            <Select
              value={boardSortColumn(sortColumn, sortOptions)}
              onChange={setSortColumn}
              options={sortOptions}
              style={{ width: '100%' }}
            />
            <Button
              icon={<FontAwesomeIcon icon={descending ? faArrowDownLong : faArrowUpLong} />}
              title={descending ? 'Descending — click for ascending' : 'Ascending — click for descending'}
              aria-label={descending ? 'Sorted descending' : 'Sorted ascending'}
              onClick={() => setSortOrder(descending ? 'ascend' : 'descend')}
            />
          </Space.Compact>
        </ControlField>
      </Col>}
      {viewToggle && <Col xl={widths.view} span={12}>
        <ControlField label="View">
          <Space size={8}>
            {viewToggle}
            {densityToggle}
          </Space>
        </ControlField>
      </Col>}
    </Row>
  </div>
}

export default JobListFilters
